import type { FC, ReactNode } from 'react'
import type { Category } from '@/types/marketplace'
import type { HeaderViewer } from '@/lib/auth/viewer'
import type { OwnerCta } from '@/lib/community/owner-cta'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { telegramConfig } from '@/constants/site'
import { START_CTA } from '@/lib/community/owner-cta'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'
import { ShopMegaMenu } from '@/components/marketplace/shop-mega-menu'
import { trackEvent } from '@/lib/analytics/client'
import { DIRECTORY_INDEX_PATH } from '@/lib/seo/telegram-directory'
import { AccountMenu, AccountPanel } from '@/components/marketplace/account-menu'

/**
 * The one Bazio header.
 *
 * Every page renders this exact component through `SiteShell`: the black
 * announcement strip, then the `bazio` wordmark hard left with the HOME /
 * MARKETPLACE / HOW BAZIO WORKS / FOR COMMUNITIES / CONTACT navigation
 * starting immediately
 * beside it and the platform action on the right, and the shared marketplace
 * mega menu.
 *
 * The wordmark is the supplied `primary-logo.png` at its own colours. It is
 * BLACK artwork, which is why this bar is cream or transparent-over-cream and
 * never a dark surface.
 *
 * The right-hand action is the platform's primary conversion path: a community
 * owner starting their own marketplace. The Money Team Albania Telegram card
 * still lives in the mobile panel, the closing band and the footer, so the
 * first community stays one tap away without owning the header.
 *
 * The strip scrolls away with the page; the navigation row itself is sticky and
 * fades to a solid cream surface with a hairline rule once the page has moved,
 * so content never reads through it.
 */

interface MtaHeaderProps {
    /** Only used by the mobile panel's shortcut list. Optional everywhere. */
    categories?: Array<Category>
    /**
     * The admin-managed announcement, when one is enabled. The artwork's own
     * line is the fallback, so the strip is never empty.
     */
    announcement?: string
    /**
     * The platform action, already resolved on the server for THIS visitor.
     *
     * It is a prop rather than something the header works out, because the
     * answer comes from the database and the header renders in the browser.
     * Omitted (a page that does not resolve it, and every signed-out visitor)
     * it is START YOUR MARKETPLACE, which is exactly what it always was.
     */
    ownerCta?: OwnerCta | null
    /**
     * The signed-in member, projected on the server by `headerViewer` from the
     * session the page guard already resolved.
     *
     * A prop for the same reason `ownerCta` is one: the answer comes from a
     * session the browser cannot read, and passing it down means the account
     * menu is in the first paint rather than appearing after a round trip.
     * Omitted, or null for a signed-out visitor, the header carries no account
     * menu — which is exactly what it carried before.
     */
    viewer?: HeaderViewer | null
}

/**
 * The Telegram card. White paper, thick rule, hard offset shadow.
 *
 * The eyebrow reads "A community on Bazio", exactly as the footer's card does,
 * because this header renders on PLATFORM surfaces (`/browse`, `/terms`,
 * `/how-it-works`). It used to say "Join Telegram" above the community's name,
 * which on those pages read as Bazio's own group rather than as one community
 * hosted on it. The founding marketplace's own storefront branding is
 * unaffected: that comes from its `CommunityContext`, not from here.
 */
const TelegramCard: FC<{ className?: string }> = ({ className }): ReactNode => (
    <a
        href={telegramConfig.communityUrl ?? 'https://t.me/moneyteamalbania'}
        target='_blank'
        rel='noreferrer noopener'
        className={cn(
            'group inline-flex items-center gap-3.5 rounded-mta-md border-2 border-ink-900 bg-paper px-5 py-3',
            'shadow-offset-sm transition-transform duration-200 ease-mta',
            'hover:-translate-y-0.5 hover:shadow-offset focus-visible:-translate-y-0.5 focus-visible:shadow-offset',
            'mta-focus',
            className
        )}
    >
        <TelegramGlyph className='size-9 shrink-0 text-ink-900' />

        <span className='flex flex-col gap-0.5 leading-none'>
            <span className='hero-label text-[12px] tracking-[0.05em] text-brand-red'>
                A community on Bazio
            </span>

            <span className='hero-label text-[18px] tracking-[0.01em] text-ink-900'>
                Money Team Albania
            </span>
        </span>
    </a>
)

/**
 * The platform action. Same plate language as the Telegram card, in the
 * interactive red so it reads as the one thing to press on the page.
 *
 * The wording and the destination both come from the resolved state, so an
 * owner who already runs a marketplace is never sent back to the application
 * form. The plate itself does not change, so the header keeps its shape.
 */
const StartCta: FC<{ cta: OwnerCta, className?: string, onClick?: () => void }> = ({ cta, className, onClick }): ReactNode => (
    <Link
        href={cta.href}
        onClick={onClick}
        className={cn(
            'group inline-flex items-center justify-center gap-3 rounded-mta-md border-2 border-ink-900 bg-brand-red-ink px-5 py-3.5',
            'shadow-offset-sm transition-transform duration-200 ease-mta',
            'hover:-translate-y-0.5 hover:shadow-offset focus-visible:-translate-y-0.5 focus-visible:shadow-offset',
            'mta-focus',
            className
        )}
    >
        <span className='hero-label text-[15px] tracking-[0.03em] text-white sm:text-[17px]'>
            {cta.label}
        </span>

        <ArrowRight
            aria-hidden='true'
            className='size-5 shrink-0 text-white transition-transform duration-200 ease-mta group-hover:translate-x-1'
        />
    </Link>
)

export const MtaHeader: FC<MtaHeaderProps> = ({ categories = [], announcement, ownerCta, viewer }): ReactNode => {
    const cta = ownerCta ?? START_CTA

    const [shopOpen, setShopOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)

    const shopRef = useRef<HTMLDivElement>(null)
    const menuButton = useRef<HTMLButtonElement>(null)
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    /* Hover opens, a short grace period closes, so the submenu feels the same
       wherever it is opened from. */
    const openShop = (): void => {
        if (closeTimer.current) clearTimeout(closeTimer.current)

        setShopOpen(true)
    }

    const closeShopSoon = (): void => {
        if (closeTimer.current) clearTimeout(closeTimer.current)

        closeTimer.current = setTimeout(() => setShopOpen(false), 170)
    }

    useEffect(() => () => {
        if (closeTimer.current) clearTimeout(closeTimer.current)
    }, [])

    /* The solid surface appears once the page has actually moved. Read after
       mount only, so the server and the first client render agree. */
    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 8)

        onScroll()

        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    /* Escape closes whichever surface is open, and a click outside closes the
       shop menu. Both are wired after mount, never during render. */
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return

            setShopOpen(false)

            setMenuOpen((open) => {
                if (open) menuButton.current?.focus()

                return false
            })
        }

        /* Anything inside the bar, trigger or panel alike, leaves it open. */
        const onPointer = (event: PointerEvent): void => {
            if (!shopRef.current || shopRef.current.contains(event.target as Node)) return

            setShopOpen(false)
        }

        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onPointer)

        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onPointer)
        }
    }, [])

    /* The mobile panel sits over the page, so the page behind it must not
       scroll while it is open. */
    useEffect(() => {
        if (!menuOpen) return

        const previous = document.body.style.overflow

        document.body.style.overflow = 'hidden'

        return () => {
            document.body.style.overflow = previous
        }
    }, [menuOpen])

    const linkClass = 'mta-focus hero-label rounded-mta-xs px-1 py-2 text-[17px] tracking-[0.01em] text-ink-900 transition-colors duration-150 hover:text-brand-red-ink'

    return (
        <>
            {/* ---- Announcement strip: black band, red stars ---- */}
            <div className='bg-ink-900'>
                <p className='mx-auto flex min-h-[46px] max-w-[1540px] items-center justify-center gap-4 px-5 py-2.5 text-center text-[11px] font-extrabold uppercase leading-tight tracking-[0.14em] text-white sm:gap-6 sm:text-[13px]'>
                    <span aria-hidden='true' className='text-brand-red'>
                        &#9733;
                    </span>

                    {announcement?.trim() || 'Listings are checked against community rules and moderation settings'}

                    <span aria-hidden='true' className='text-brand-red'>
                        &#9733;
                    </span>
                </p>
            </div>

            {/* The sticky bar hosts the shared SHOP submenu, so it is the
                positioning context and it carries the open state the panel
                reads. */}
            <header
                ref={shopRef}
                className={cn(
                    'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ease-mta',
                    shopOpen ? 'bg-canvas' : scrolled ? 'bg-canvas' : 'bg-transparent',
                    scrolled || shopOpen
                        ? 'border-ink-900/10 shadow-[0_6px_18px_-14px_rgba(10,10,10,0.45)]'
                        : 'border-transparent shadow-none',
                    shopOpen && 'shop-open'
                )}
            >
                {/* ---- Main navigation ---- */}
                <nav aria-label='Primary' className='relative z-50 mx-auto flex w-full max-w-[1540px] items-center gap-4 px-5 py-4 sm:px-8 lg:gap-8 lg:py-5 lg:px-[clamp(24px,4vw,72px)]'>
                    {/* The mark leads the bar, hard left, with the navigation
                        starting immediately beside it. */}
                    <Link
                        href='/'
                        aria-label='Bazio home'
                        className='mta-focus shrink-0 rounded-mta-xs'
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src='/images/brand/primary-logo.png'
                            alt='Bazio'
                            width={2172}
                            height={724}
                            className='h-7 w-auto select-none object-contain sm:h-8'
                        />
                    </Link>

                    {/* Takes the slack on every breakpoint, so the action group
                        stays hard right whether or not the list is shown. */}
                    <div className='flex flex-1 items-center'>
                        <ul className='hidden items-center gap-8 lg:flex lg:gap-10'>
                            <li>
                                <Link href='/' className={linkClass}>
                                    Home
                                </Link>
                            </li>

                            <li>
                                <div
                                    className='relative'
                                    onMouseEnter={openShop}
                                    onMouseLeave={closeShopSoon}
                                >
                                    <button
                                        type='button'
                                        onClick={() => setShopOpen((open) => !open)}
                                        aria-expanded={shopOpen}
                                        aria-controls='shop-mega-menu'
                                        className={cn(
                                            linkClass,
                                            'inline-flex items-center gap-1.5',
                                            shopOpen && 'text-brand-red-ink'
                                        )}
                                    >
                                        Marketplace

                                        <ChevronDown
                                            aria-hidden='true'
                                            className={cn('size-4 transition-transform duration-200', shopOpen && 'rotate-180')}
                                        />
                                    </button>
                                </div>
                            </li>

                            <li>
                                <Link href='/for-communities' className={linkClass}>
                                    For communities
                                </Link>
                            </li>

                            <li>
                                <Link href='/contact' className={linkClass}>
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/*
                        The platform action and, when there is one, the account.
                        Below 640px the red plate is hidden and the account sits
                        here on its own, so a phone still reaches it straight
                        from the bar without opening the sheet.
                    */}
                    <div className='flex items-center justify-end gap-2.5 sm:gap-3'>
                        <StartCta cta={cta} className='hidden sm:inline-flex' />

                        {viewer && <AccountMenu viewer={viewer} />}

                        <button
                            ref={menuButton}
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-expanded={menuOpen}
                            aria-controls='mta-mobile-menu'
                            className='mta-focus inline-flex items-center gap-2 rounded-mta-sm border-2 border-ink-900 bg-paper px-3 py-2 text-[13px] font-extrabold uppercase tracking-[0.08em] text-ink-900 lg:hidden'
                        >
                            <Menu className='size-5' aria-hidden='true' />
                            Menu
                        </button>
                    </div>
                </nav>

                {/* The one shared SHOP submenu. */}
                <ShopMegaMenu
                    className='mega-flow'
                    onMouseEnter={openShop}
                    onMouseLeave={closeShopSoon}
                    onNavigate={() => setShopOpen(false)}
                />
            </header>

            {/* ---- Mobile panel ---- */}
            {menuOpen && (
                <div className='fixed inset-0 z-[100] lg:hidden'>
                    <button
                        type='button'
                        aria-label='Close menu'
                        onClick={() => setMenuOpen(false)}
                        className='absolute inset-0 h-full w-full cursor-default bg-ink-900/40'
                    />

                    <div
                        id='mta-mobile-menu'
                        /* The sheet now carries an account block as well, so it
                           can outgrow a short screen. Capping it and letting it
                           scroll keeps every row, and the focused one, reachable
                           instead of running off the bottom. */
                        className='absolute inset-x-3 top-3 max-h-[calc(100svh-1.5rem)] overflow-y-auto overscroll-contain rounded-mta-lg border-2 border-ink-900 bg-canvas p-5 shadow-offset'
                    >
                        <div className='flex items-center justify-between'>
                            <span className='text-[13px] font-extrabold uppercase tracking-[0.14em] text-text-muted'>
                                Menu
                            </span>

                            <button
                                type='button'
                                onClick={() => {
                                    setMenuOpen(false)
                                    menuButton.current?.focus()
                                }}
                                className='mta-focus inline-flex size-11 items-center justify-center rounded-mta-sm border-2 border-ink-900 bg-paper text-ink-900'
                                aria-label='Close menu'
                            >
                                <X className='size-5' aria-hidden='true' />
                            </button>
                        </div>

                        <ul className='mt-4 grid gap-1'>
                            {[
                                { href: '/', label: 'Home' },
                                { href: '/browse', label: 'Shop all listings' },
                                { href: DIRECTORY_INDEX_PATH, label: 'Telegram Marketplaces' },
                                { href: '/sell', label: 'Start selling' },
                                { href: '/for-communities', label: 'For communities' },
                                { href: '/contact', label: 'Contact' },
                                /* Only offered to somebody who is not already
                                   signed in. Their own account replaces it. */
                                ...(viewer ? [] : [{ href: '/signin', label: 'Sign in' }])
                            ].map((item) => (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        onClick={() => {
                                            if (item.href === DIRECTORY_INDEX_PATH) {
                                                trackEvent({ type: 'telegram_directory_entry_click', metadata: { source: 'marketplace_nav' } })
                                            }

                                            setMenuOpen(false)
                                        }}
                                        className='mta-focus block rounded-mta-sm px-3 py-3.5 text-base font-extrabold uppercase tracking-[0.04em] text-ink-900 transition-colors hover:bg-surface-muted'
                                    >
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        {/* The account, written out rather than nested as a
                            second dropdown: this sheet renders above the popup
                            layer, so a menu opened here would sit behind it. */}
                        {viewer && (
                            <AccountPanel
                                viewer={viewer}
                                onNavigate={() => setMenuOpen(false)}
                            />
                        )}

                        {categories.length > 0 && (
                            <ul className='mt-3 grid gap-1 border-t-2 border-ink-900/15 pt-3'>
                                {categories.slice(0, 6).map((category) => (
                                    <li key={category.id}>
                                        <Link
                                            href={`/category/${category.slug}`}
                                            onClick={() => setMenuOpen(false)}
                                            className='mta-focus block rounded-mta-sm px-3 py-3 text-sm font-medium text-ink-900 transition-colors hover:bg-surface-muted'
                                        >
                                            {category.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/*
                            The bar's own plate is hidden below 640px, so on a
                            phone this panel is the only place the platform
                            action appears. It carries the same resolved state.
                        */}
                        <StartCta
                            cta={cta}
                            onClick={() => setMenuOpen(false)}
                            className='mt-4 flex w-full sm:hidden'
                        />

                        <TelegramCard className='mt-4 w-full' />
                    </div>
                </div>
            )}
        </>
    )
}
